import React from 'react'
import { InferType } from 'yup'
import { captureException } from '@sentry/react'
import { useMutation } from '@tanstack/react-query'
import { Modal } from '../../../common/Modal'
import { ModalHeader } from '../../../common/Modal/ModalHeader'
import { ModalBody } from '../../../common/Modal/ModalBody'
import { useAppTranslation } from '../../../../hooks/useAppTranslation'
import { CreateUpdateContractForm } from '../CreateUpdateContractForm'
import { ToastType } from '../../../common/Toast/toast-type'
import { useToastNotifications } from '../../../../hooks/useToastNotification'
import { societyContractCreateSchema } from '../../../../forms/schemas/society_contract_create'
import { IContractWithFiles } from '../../../../interfaces/services/contracts.interfaces'
import { createContract, updateContract } from '../../../../api/contracts'
import { useCurrentSociety } from '../../../../hooks/useCurrentSociety'

interface CreateUpdateContractModalProps {
  show: boolean
  close: () => void
  children?: React.ReactNode
  contract?: IContractWithFiles
}

export const CreateUpdateContractModal = ({
  contract,
  children,
  show,
  close,
}: CreateUpdateContractModalProps): React.ReactNode => {
  const { translate } = useAppTranslation()
  const { setToastNotification } = useToastNotifications()
  const { society } = useCurrentSociety()

  const updateMode = !!contract

  const showErrorMessage = (): void => {
    const message = updateMode
      ? 'createUpdateContractModal.flashMessage.updateContractFailure'
      : 'createUpdateContractModal.flashMessage.createContractFailure'
    setToastNotification(ToastType.DANGER, translate(message))
  }

  const showSuccessMessage = (): void => {
    const message = updateMode
      ? 'createUpdateContractModal.flashMessage.updateContractSuccess'
      : 'createUpdateContractModal.flashMessage.createContractSuccess'
    setToastNotification(ToastType.SUCCESS, translate(message))
  }

  const { mutate: mutateCreateContract, isPending: isCreatingContract } =
    useMutation({
      mutationFn: createContract,
      onSuccess: () => {
        close()
        showSuccessMessage()
      },
      onError: () => {
        showErrorMessage()
      },
    })
  const { mutate: mutateUpdateContract, isPending: isUpdatingContract } =
    useMutation({
      mutationFn: updateContract,
      onSuccess: () => {
        close()
        showSuccessMessage()
      },
      onError: () => {
        showErrorMessage()
      },
    })
  const onSubmit = async (
    data: InferType<typeof societyContractCreateSchema>
  ): Promise<void> => {
    const _data = {
      ...data,
      documentIds: data.documentIds.map((document) => document.id),
    }
    if (updateMode) {
      mutateUpdateContract({ id: contract._id, body: _data })
    } else {
      mutateCreateContract(_data)
    }
  }

  const onError = (error: unknown): void => {
    captureException(error)
    showErrorMessage()
  }

  const loading = isCreatingContract || isUpdatingContract

  return (
    <>
      {children}
      <Modal show={show}>
        {{
          header: (
            <ModalHeader onClose={close}>
              {translate(
                updateMode
                  ? 'createUpdateContractModal.editContractTitle'
                  : 'createUpdateContractModal.addNewContractTitle'
              )}
            </ModalHeader>
          ),
          body: (
            <ModalBody>
              {society && (
                <CreateUpdateContractForm
                  society={society}
                  onError={onError}
                  onSubmit={onSubmit}
                  onClose={close}
                  loading={loading}
                  contract={contract}
                  documents={contract?.documents}
                />
              )}
            </ModalBody>
          ),
        }}
      </Modal>
    </>
  )
}
